import React, { Dispatch, SetStateAction, useState } from 'react';
import { Dropdown } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Company } from '../../models/company.model';
import { AccountingAccount } from '../../models/accounting.account.model';
import { RequirementContent } from '../../models/requirement.content.model';
import { accountingAccountSelected } from '../../scripts/list.selected.item.script';
import { legendValid } from '../../tools/legend.data.entry.tool';
import { prefixLegend } from '../../scripts/name.legend.script';
import { accountingAccountFormat } from '../../scripts/format.value.script';

import AppLegend from './Legend';

export interface AppInputAccountingAccountProps {
  component?: string,
  options: {small: boolean, type: string | null, index: number | null, item: {content: RequirementContent, accounting_account_debit: string, accounting_account_credit: string} | null},
  companyForUser: Company,
  accountingAccountsForCompany?: AccountingAccount[],
  accountingExpenseAccountsForCompany?: AccountingAccount[],
  accountingVatAccountsForCompany?: AccountingAccount[],
  accountingProductAccountsForCompany?: AccountingAccount[],
  accounts?: {value: {expense: string, vat: string, product: string}, valid: {expense: boolean, vat: boolean, product: boolean}},
  contentAccounts?: {value: {content: RequirementContent, accounting_account_debit: string, accounting_account_credit: string}[], valid: boolean[]},
  setAccounts?: Dispatch<SetStateAction<{value: {expense: string, vat: string, product: string}, valid: {expense: boolean, vat: boolean, product: boolean}}>>,
  setContentAccounts?: Dispatch<SetStateAction<{value: {content: RequirementContent, accounting_account_debit: string, accounting_account_credit: string}[], valid: boolean[]}>>,
  accountingAccountRebate?: Function
};

const AppInputAccountingAccount: React.FunctionComponent<AppInputAccountingAccountProps> = ({component, options, companyForUser, accountingAccountsForCompany, accountingExpenseAccountsForCompany, accountingVatAccountsForCompany, accountingProductAccountsForCompany, accounts, contentAccounts, setAccounts, setContentAccounts, accountingAccountRebate}) => {
  const {lang} = StateLanguage()

  const [search, setSearch] = useState<{text: string, list: AccountingAccount[]}>({text: '', list: []})

  const handleChangeSearchAccount = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let current: AccountingAccount[] = accountingAccountList()
    let list: AccountingAccount[] = []
    let text: string = event.target.value

    if (text.length > 0) {
      for (let account of current) {
        if (account.code.indexOf(text) !== -1 || accountingAccountFormat(account, companyForUser.language).toLowerCase().indexOf(text.toLowerCase()) !== -1) {
          list.push(account)
        }
      }
    } else {
      list = current
    }

    setSearch({text: text, list: list})
  }

  const handleChangeAccountingAccount = (item: string) => {
    if (options.type && accounts && setAccounts) {
      setAccounts(accounts => ({value: {...accounts.value, [options.type as 'expense' | 'vat' | 'product']: item}, valid: {...accounts.valid, [options.type as 'expense' | 'vat' | 'product']: true}}))
      legendValid(`${prefixLegend(component)}legend-account-${options.type}-required`)
    }
  }

  const handleChangeAccountingAccounts = (item: AccountingAccount) => {
    if (contentAccounts && setContentAccounts && accountingAccountRebate) {
      let tempContentAccountsValue = contentAccounts.value.map((temp_item, temp_index) => {
        if (options.index === temp_index) {
          temp_item.accounting_account_debit = (temp_item.content.rebate.type !== 'none') ? accountingAccountRebate(item.id) : ''
          temp_item.accounting_account_credit = item.id
        }
        return temp_item
      })
      let tempContentAccountsValid = contentAccounts.valid.map((temp_item, temp_index) => {
        if (options.index === temp_index) {
          temp_item = true
        }
        return temp_item
      })

      setContentAccounts({value: tempContentAccountsValue, valid: tempContentAccountsValid})
    }
  }

  function nameAccountingAccount(): string {
    switch (options.type) {
      case 'expense':
        return "account-expense"
      case 'vat':
        return "account-vat"
      case 'product':
        return "account-product"
      default:
        return "account"
    }
  }

  function accountingAccountList(): AccountingAccount[] {
    switch (options.type) {
      case 'expense':
        return accountingExpenseAccountsForCompany || []
      case 'vat':
        return accountingVatAccountsForCompany || []
      case 'product':
        return accountingProductAccountsForCompany || []
      default:
        return []
    }
  }

  function uploadDropdown() {
    setSearch({text: '', list: accountingAccountList()})
  }

  return (
    <>
      <Dropdown onToggle={uploadDropdown}>
        <Dropdown.Toggle variant={`select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open ${options.small ? "w-300px" : "w-100"} p-0`}>
          <span className="selection">
            <span className={`select2-selection select2-selection--single form-select form-select-solid ${options.small && "form-select-sm"}`}>
              <span className="select2-selection__rendered" role="textbox">
                { options.type !== null && accounts && setAccounts &&
                  <>
                    {accounts.valid[options.type as 'expense' | 'vat' | 'product'] ? accountingAccountSelected(lang, accountingAccountList(), accounts.value[options.type as 'expense' | 'vat' | 'product'], companyForUser.language) : lang.labels.selectOption}
                  </>
                }
                { options.index !== null && options.item !== null && accountingAccountsForCompany && contentAccounts && setContentAccounts &&
                  <>
                    {contentAccounts.valid[options.index] ? accountingAccountSelected(lang, accountingAccountsForCompany, options.item.content.price_unit < 0 ? options.item.accounting_account_debit : options.item.accounting_account_credit, companyForUser.language) : lang.labels.selectOption}
                  </>
                }
              </span>
            </span>
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
          <span className="select2-dropdown select2-dropdown--below">
            <span className="select2-results">
              <ul className="select2-results__options" role="listbox">
                { accountingAccountsForCompany &&
                  <li className={`select2-results__option select2-results__option--selectable ${accountingAccountsForCompany.length > 0 && "d-none"}`}>
                    <Dropdown.Item bsPrefix="select2-results__option__text py-2">{lang.labels.selectOption}</Dropdown.Item>
                  </li>
                }
                { accounts && setAccounts &&
                  <>
                    <span className="select2-search select2-search--dropdown">
                      <input className="select2-search__field" type="text" autoComplete="off" name={nameAccountingAccount()} value={search.text} onChange={handleChangeSearchAccount} />
                    </span>
                    { search.list.length > 0
                      ?
                      <>
                        { search.list.map (( (item, index) => { return (
                          <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === accounts.value.expense && "select2-results__option--selected"}`} role="option" aria-selected={item.id === accounts.value.expense}>
                            <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeAccountingAccount(item.id)}>{accountingAccountFormat(item, companyForUser.language)}</Dropdown.Item>
                          </li>
                        )}))}
                      </>
                      :
                      <li className="select2-results__option select2-results__message" role="alert">{lang.labels.noResultsFound}</li>
                    }
                  </>
                }
                { options.item !== null && accountingAccountsForCompany &&
                  <>
                    { accountingAccountsForCompany.map (( (item, index) => { return (
                      <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === (options.item!.content.price_unit < 0 ? options.item!.accounting_account_debit : options.item!.accounting_account_credit) && "select2-results__option--selected"}`} role="option" aria-selected={item.id === (options.item!.content.price_unit < 0 ? options.item!.accounting_account_debit : options.item!.accounting_account_credit)}>
                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeAccountingAccounts(item)}>{accountingAccountFormat(item, companyForUser.language)}</Dropdown.Item>
                      </li>
                    )}))}
                  </>
                }
              </ul>
            </span>
          </span>
        </Dropdown.Menu>
      </Dropdown>
      { accounts && setAccounts &&
        <AppLegend component={component} attribute={{check: accounts.valid.expense, name: nameAccountingAccount(), index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
      }
    </>
  )
}

export default AppInputAccountingAccount;
